import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Add axios interceptor to automatically include Authorization header
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { username, password });
    return response.data;
};

export const getConfig = (type) => {
    return axios.get(`${API_URL}/config/${type}`);
};

export const saveConfig = (type, data) => {
    return axios.post(`${API_URL}/config/${type}`, data);
};

export const uploadFile = (tipo, file, mes = null) => {
    const formData = new FormData();
    formData.append('file', file);

    // Production uploads go to dedicated endpoint with 'competencia' parameter
    if (tipo === 'producao') {
        if (mes) {
            formData.append('competencia', mes);
        }
        return axios.post(`${API_URL}/production/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }

    // Other uploads (balancete, etc.) use the standard endpoint with 'mes' parameter
    if (mes) {
        formData.append('mes', mes);
    }
    return axios.post(`${API_URL}/upload_${tipo}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const processMonth = (data) => {
    return axios.post(`${API_URL}/processar_mes`, data);
};

export const getDashboardData = (mes = null, startMonth = null, endMonth = null) => {
    const params = {};
    if (mes) params.mes = mes;
    if (startMonth) params.start_month = startMonth;
    if (endMonth) params.end_month = endMonth;
    return axios.get(`${API_URL}/dashboard/ultimo_resultado`, { params });
};
export const getDashboardMonths = () => {
    return axios.get(`${API_URL}/dashboard/months`);
};

export const createCompetence = (mes, company_id = null) => {
    return axios.post(`${API_URL}/competence`, { mes, company_id });
};

export const getCompetences = () => {
    return axios.get(`${API_URL}/competences`);
};

export const closeCompetence = (mes, company_id = null) => {
    return axios.post(`${API_URL}/competences/close`, { mes, company_id });
};

export const reopenCompetence = (mes, company_id = null) => {
    return axios.post(`${API_URL}/competences/reopen`, { mes, company_id });
};

export const deleteCompetence = (mes, company_id = null) => {
    return axios.delete(`${API_URL}/competences`, { data: { mes, company_id } });
};

export const validateCompetence = (mes) => {
    return axios.get(`${API_URL}/competences/${mes}/validate`);
};

export const reprocessCompetence = (mes) => {
    return axios.post(`${API_URL}/competences/${mes}/reprocess`);
};

export const getBalancete = (mes) => {
    return axios.get(`${API_URL}/balancete`, { params: { mes } });
};

export const getHistoricoCustos = () => {
    return axios.get(`${API_URL}/historico_custos`);
};

export const analisarAnomalias = (data) => {
    return axios.post(`${API_URL}/analisar_anomalias`, data);
};

export const getAIAnalysis = (params) => {
    return axios.get(`${API_URL}/ai/analysis`, { params });
};

export const getAIChat = (message, context = {}) => {
    return axios.post(`${API_URL}/ai/chat`, { message, context });
};

export const getAIInsights = () => {
    return axios.get(`${API_URL}/ai/insights`);
};

export const getProductionInsights = () => {
    return axios.get(`${API_URL}/ai/production_insights`);
};

export const generateAIReport = (data) => {
    return axios.post(`${API_URL}/ai/report`, data);
};

export const getCostForecast = () => {
    return axios.get(`${API_URL}/ai/forecast`);
};

export const getSeasonality = () => {
    return axios.get(`${API_URL}/ai/seasonality`);
};

export const getDashboard = () => {
    return axios.get(`${API_URL}/ai/dashboard`);
};

export const getDrilldown = (centro) => {
    return axios.get(`${API_URL}/ai/drilldown/${encodeURIComponent(centro)}`);
};

export const getClustering = () => {
    return axios.get(`${API_URL}/ai/clustering`);
};

export const getAnomalies = () => {
    return axios.get(`${API_URL}/ai/anomalies`);
};

export const getRecommendations = () => {
    return axios.get(`${API_URL}/ai/recommendations`);
};

export const getOptimizationPlan = () => {
    return axios.get(`${API_URL}/ai/optimization-plan`);
};

export const sendAssistantMessage = (message, page) => {
    return axios.post(`${API_URL}/ai/assistant`, { message, page });
};

export const getSupplies = (competencia) => {
    return axios.get(`${API_URL}/supplies`, { params: { competencia } });
};

export const saveSupplies = (competencia, data) => {
    return axios.post(`${API_URL}/supplies/${competencia}`, data);
};

export const getProduction = (competencia) => {
    return axios.get(`${API_URL}/production`, { params: { competencia } });
};

export const saveProduction = (competencia, data) => {
    return axios.post(`${API_URL}/production/${competencia}`, data);
};

export const deleteHistoricoMes = (mes) => {
    return axios.post(`${API_URL}/historico/excluir_mes`, { mes });
};

export const getUltimoBalancete = () => {
    return axios.get(`${API_URL}/ultimo_balancete`);
};

export const runSimulation = (data) => {
    return axios.post(`${API_URL}/simulation`, data);
};

// ========== COMPANIES API ==========
export const getCompanies = () => {
    const token = localStorage.getItem('token');
    return axios.get(`${API_URL}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const createCompany = (data) => {
    const token = localStorage.getItem('token');
    return axios.post(`${API_URL}/companies`, data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const updateCompany = (id, data) => {
    const token = localStorage.getItem('token');
    return axios.put(`${API_URL}/companies/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const deleteCompany = (id) => {
    const token = localStorage.getItem('token');
    return axios.delete(`${API_URL}/companies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

export const getCompany = (id) => {
    const token = localStorage.getItem('token');
    return axios.get(`${API_URL}/companies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
};

// Check for unregistered cost centers in balancete
export const checkMissingCostCenters = (filename) => {
    return axios.get(`${API_URL}/verificar_centros_nao_cadastrados`, {
        params: { filename }
    });
};

export const getMissingCostCenters = () => {
    return axios.get(`${API_URL}/centros_custo/faltantes`);
};

export const getMissingAccounts = () => {
    return axios.get(`${API_URL}/plano_contas/faltantes`);
};

// Supplies API
export const deleteSupply = (id) => {
    return axios.post(`${API_URL}/supplies/delete`, { id });
};

// Balancete CRUD
export const addBalanceteItem = (data) => {
    return axios.post(`${API_URL}/balancete/item`, data);
};

export const updateBalanceteItem = (id, data) => {
    return axios.put(`${API_URL}/balancete/item/${id}`, data);
};

export const deleteBalanceteItem = (id) => {
    return axios.delete(`${API_URL}/balancete/item/${id}`);
};

// Production CRUD
export const addProductionItem = (data) => {
    return axios.post(`${API_URL}/production/item`, data);
};

export const updateProductionItem = (id, data) => {
    return axios.put(`${API_URL}/production/item/${id}`, data);
};

export const deleteProductionItem = (id) => {
    return axios.delete(`${API_URL}/production/item/${id}`);
};

// Supplies CRUD
export const addSupplyItem = (data) => {
    return axios.post(`${API_URL}/supplies/item`, data);
};

export const updateSupplyItem = (id, data) => {
    return axios.put(`${API_URL}/supplies/item/${id}`, data);
};

export const deleteSupplyItem = (id) => {
    return axios.delete(`${API_URL}/supplies/item/${id}`);
};

// Bulk Delete by Competence
export const deleteBalanceteCompetencia = (mes) => {
    return axios.delete(`${API_URL}/balancete/competencia/${mes}`);
};

export const deleteProductionCompetencia = (competencia) => {
    return axios.delete(`${API_URL}/production/competencia/${competencia}`);
};

export const deleteSuppliesCompetencia = (competencia) => {
    return axios.delete(`${API_URL}/supplies/competencia/${competencia}`);
};

export const exportDashboard = (params) => {
    return axios.get(`${API_URL}/dashboard/exportar`, {
        params: params,
        responseType: 'blob'
    });
};

// Budget Management API
export const getBudget = (competencia) => {
    return axios.get(`${API_URL}/budget`, { params: { competencia } });
};

export const saveBudget = (data) => {
    return axios.post(`${API_URL}/budget`, data);
};

export const deleteBudget = (competencia) => {
    return axios.delete(`${API_URL}/budget`, { params: { competencia } });
};

export const deleteBudgetItem = (competencia, centro_custo, conta) => {
    return axios.delete(`${API_URL}/budget`, {
        params: { competencia, centro_custo, conta }
    });
};



export const uploadBudget = (formData) => {
    return axios.post(`${API_URL}/budget/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const getBudgetVariance = (competencia) => {
    return axios.get(`${API_URL}/budget/variance`, { params: { competencia } });
};

export const getThresholds = () => {
    return axios.get(`${API_URL}/budget/thresholds`);
};

export const saveThreshold = (data) => {
    return axios.post(`${API_URL}/budget/thresholds`, data);
};

export const downloadBudgetTemplate = () => {
    return axios.get(`${API_URL}/budget/template`, { responseType: 'blob' });
};



// Monitoring and Data Quality API
export const triggerValidation = (competence) => {
    return axios.post(`${API_URL}/monitoring/validate`, { competence });
};

export const getValidationResults = (competence) => {
    return axios.get(`${API_URL}/monitoring/results`, { params: { competence } });
};

export const getPersistentAnomalies = (competence) => {
    return axios.get(`${API_URL}/monitoring/anomalies`, { params: { competence } });
};

export const updateAnomalyStatus = (id, status, notes) => {
    return axios.patch(`${API_URL}/monitoring/anomalies/${id}`, { status, resolution_notes: notes });
};

// Sprint 4: Service Lines API
export const getServiceLines = () => {
    return axios.get(`${API_URL}/service_lines/`);
};

export const addServiceLine = (data) => {
    return axios.post(`${API_URL}/service_lines/`, data);
};

export const updateServiceLine = (id, data) => {
    return axios.put(`${API_URL}/service_lines/${id}`, data);
};

export const deleteServiceLine = (id) => {
    return axios.delete(`${API_URL}/service_lines/${id}`);
};

export const getServiceLineMapping = () => {
    return axios.get(`${API_URL}/service_lines/mapping`);
};

export const updateServiceLineMapping = (service_line_id, cost_centers) => {
    return axios.post(`${API_URL}/service_lines/mapping`, { service_line_id, cost_centers });
};

export const getCostCenters = () => {
    return axios.get(`${API_URL}/service_lines/cost_centers`);
};

// ========== COST TABLES API ==========
export const getCostTable = (competencia, params = {}) => {
    return axios.get(`${API_URL}/cost_tables/${competencia}`, { params });
};

export const getCostTableStatus = (competencia) => {
    return axios.get(`${API_URL}/cost_tables/${competencia}/status`);
};

// ========== STANDARD COSTS API ==========
export const getStandardCosts = (competencia, params = {}) => {
    return axios.get(`${API_URL}/standard_costs`, { params: { competencia, ...params } });
};

export const addStandardCostItem = (data) => {
    return axios.post(`${API_URL}/standard_costs/item`, data);
};

export const updateStandardCostItem = (id, data) => {
    return axios.put(`${API_URL}/standard_costs/item/${id}`, data);
};

export const deleteStandardCostItem = (id) => {
    return axios.delete(`${API_URL}/standard_costs/item/${id}`);
};

export const duplicateStandardCosts = (data) => {
    return axios.post(`${API_URL}/standard_costs/duplicate`, data);
};

export default axios;
